import { Component, h, Host, Listen, Prop } from '@stencil/core';
import { QueryParamsHelpers } from '../../../../../shared/helpers/query-params.helpers';
import { TrackerEventsEnum } from '../../../../../shared/models/tracker-events.enum';
import { sdkLogHelper } from '../../../../../shared/helpers/sdk-log.helper';
import { get as getState, set as setState } from './product.store';
import { IProductSubscriptions } from './product-subscriptions/product-subscriptions';
import get from 'lodash/get';
import { IProduct } from '../../../../../shared/models/checkout-submitter-product.interface';

enum CheckoutTypeEnum {
  subscription = 'subscription',
  oneTime = 'oneTime'
}

@Component({
  tag: 'product-checkout-submitter',
  styleUrl: 'product-checkout-submitter.scss',
})
export class ProductCheckoutSubmitter {
  @Prop() subscriptionsJson: string;
  @Prop() mostPopular: string;
  @Prop() productJson: string;
  @Prop() dosageJson: string;

  @Listen('productCTAClicked', { target: 'document' })
  productCTAClicked() {
    document.getElementById('submit-subscription-btn').click();
  }

  subscriptions: IProductSubscriptions;
  product: IProduct;
  type: CheckoutTypeEnum;
  qty: number;
  defaultOneTimeQty: number = 1;
  isEasymeds: boolean = window['apomedsConfigs'].isEasymeds;

  translate = (key) => {
    return get(window['pageActionsContentJson'], key, key);
  }

  get isSubscriptionTouLinkShown(): boolean {
    return getState('checkoutType') === CheckoutTypeEnum.subscription
      && !!getState('subscription');
  }

  componentWillLoad() {
    this.product = JSON.parse(this.productJson);

    setState('mostPopular', this.mostPopular);
    setState('product', this.product);
    setState('dosages', JSON.parse(this.dosageJson));
    setState('oneTimePrice', this.product.modifications[0]?.one_time_price);
    setState('msrp', this.product.modifications[0]?.msrp);
    setState('maxSaleQty', this.product.modifications[0]?.max_sale_qty);

    this.initCheckoutTabTypeAndQty();
    this.processingSubscriptions();
    this.removeQueryParamsFromLinks(['qty', 'type', 'sp']);
    this.setLogs(TrackerEventsEnum.initOTCProductPage);
  }

  removeQueryParamsFromLinks(paramsToRemove: string[]) {
    //we don't need to save query params from product page to others
    const links = document.getElementsByTagName('a');

    Array.from(links).forEach((link) => {
      const href = link.getAttribute('href');

      if (href) {
        link.setAttribute('href', QueryParamsHelpers.getLinkWithoutCertainQueryParams(href, paramsToRemove));
      }
    });
  }

  initCheckoutTabTypeAndQty() {
    const existedQueryParams = QueryParamsHelpers.queryParams;
    this.subscriptions = JSON.parse(this.subscriptionsJson);
    let initialCheckoutType = this.subscriptions?.value?.length ? CheckoutTypeEnum.subscription : CheckoutTypeEnum.oneTime;

    setState('qty', Number(existedQueryParams?.qty || this.defaultOneTimeQty));
    setState('checkoutType', CheckoutTypeEnum[existedQueryParams.type] || initialCheckoutType);
  }

  processingSubscriptions() {
    this.subscriptions = JSON.parse(this.subscriptionsJson);
    this.subscriptions.value = this.subscriptions?.value.sort((subA, subB) => subA['period_interval'] - subB['period_interval'] );

    setState('subscriptions', this.subscriptions);
    setState('subscription', this.getDefaultSubscription(QueryParamsHelpers.queryParams));
  }

  getDefaultSubscription(queryParamsObj: { [key: string]: string }): string {
    if (!this.subscriptions?.value?.length) return null;

    if (queryParamsObj?.sp && sessionStorage.getItem('orderSubmitted')) return queryParamsObj?.sp;

    if (this.mostPopular) return this.subscriptions?.value.find((sub) => sub.period === this.mostPopular).period;

    return this.subscriptions?.value[0].period;
  }

  generateUrl() {
    if(this.submitDisabled()) return '#';

    const existedQueryParams = QueryParamsHelpers.queryParams;
    const type = getState('checkoutType');
    const queryParamsString = QueryParamsHelpers.queryParamsToString(
      {
        category_id: this.product['category_id'],
        product_id: this.product['id'],
        modification_id: this.product['modifications'][0].id,
        type: type,
        ...(type === CheckoutTypeEnum.oneTime && { start_order: 'true' }),
        ...(existedQueryParams['dpg'] && { dpg: existedQueryParams['dpg'] }),
        ...(existedQueryParams['authToken'] && { authToken: existedQueryParams['authToken'] }),
        ...(existedQueryParams['utm_source'] && { utm_source: existedQueryParams['utm_source'] }),
        ...(type === CheckoutTypeEnum.subscription && { sp: getState('subscription') }),
        ...(type === CheckoutTypeEnum.subscription && { qty: '1' }),
        ...(type === CheckoutTypeEnum.oneTime && { qty: getState('qty').toString() }),
      }).replace(/^\?/, '');

    return `${window.location.origin}${this.isEasymeds ? '' : '/' + window['apomedsConfigs']?.region }/subscription?${queryParamsString}`;
  }

  setLogs(eventName: TrackerEventsEnum, context = {}) {
    sdkLogHelper(eventName, context);
  }

  submitDisabled() {
    return getState('checkoutType') === CheckoutTypeEnum.subscription && !getState('subscription');
  }

  onTabClick(type: CheckoutTypeEnum) {
    if (type === getState('checkoutType')) return;

    sdkLogHelper(type === CheckoutTypeEnum.oneTime ? TrackerEventsEnum.oneTimeChosen : TrackerEventsEnum.subscriptionChosen, {
      query_params: window.location.search
    });

    this.qty = getState('qty');

    setState('checkoutType', type)
  }

  toggleTermsOfUseModal(isShown: boolean) {
    setState('termsOfUseModalFlag', isShown);
  }

  render() {
    return <Host>
      <form>
        <div class="product-cta-tabs">
          <div
            data-test="subscription-tab"
            class={`
              tab
              ${getState('checkoutType') === CheckoutTypeEnum.subscription ? 'active' : ''}
              ${!this.subscriptions.value.length ? 'disabled' : ''}
            `}
            onClick={() => this.subscriptions.value.length && this.onTabClick(CheckoutTypeEnum.subscription)}
          >
            <h2>{this.translate('subscribeTo')}</h2>
            <div class="arrow"></div>
          </div>
          <div
            data-test="one-time-tab"
            class={`tab ${getState('checkoutType') === CheckoutTypeEnum.oneTime ? 'active' : ''}`}
            onClick={() => this.onTabClick(CheckoutTypeEnum.oneTime)}
          >
            <h2>{this.translate('oneTimePurchase')}</h2>
            <div class="arrow"></div>
          </div>
        </div>
        <div class="benefits">
          <div class="benefit">
            <img src="/img/product-page/secure_benefit.svg"
                 alt="Secure benefit"
                 width="32"
                 height="32"
            />
            <span>{this.translate('securePayment')}</span>
          </div>
          {getState('checkoutType') === CheckoutTypeEnum.subscription ?
            <div class="benefit">
              <img src="/img/product-page/calendar_benefit.svg"
                   alt="Calendar benefit"
                   width="32"
                   height="32"
              />
              <span>{this.translate('cancelAnyTime')}</span>
            </div>
            : ''}
        </div>
        <div class="checkout-content">
          {
            getState('checkoutType') === CheckoutTypeEnum.subscription ?
              <product-subscriptions></product-subscriptions> :
              <product-one-time-buy></product-one-time-buy>
          }
        </div>
        <div class="advantages-container">
          <div class="advantages">
            <div class="gift">
              <img src="/img/product-page/gift.svg"
                   alt="Free shipping"
                   width="32"
                   height="32"
              />
              <span>{this.translate('freeShipping')}</span>
            </div>
            <div class="delivery">
              <img src="/img/product-page/truck.svg"
                   alt="Delivery at"
                   width="32"
                   height="32"
              />
              <span>{this.translate('expectedDelivery')} <span
                class="date">{this.product?.delivered_at}</span></span>
            </div>
          </div>
        </div>

        <a class={`btn-orange btn-subscribe ${this.submitDisabled() ? 'btn-subscribe-disabled' : ''}`}
           rel='nofollow'
           id='submit-subscription-btn'
           onClick={() => this.setLogs(TrackerEventsEnum.choosePlanOTC, { sp: getState('subscription') })}
           href={this.generateUrl()}>
          {this.translate('proceedToCheckout')} <i class="icon icon-long-arrow-alt-right-solid"></i>
        </a>

        {this.isSubscriptionTouLinkShown
          &&
          <div class="subscription-tou">
            {this.translate('clickingIAgree')} <a onClick={() => this.toggleTermsOfUseModal(true)}>
              {this.translate('subscriptionTermsOfUseLink.title')}
            </a>
          </div>
        }
        {getState('termsOfUseModalFlag')
          && <static-page-modal page-url={this.translate('subscriptionTermsOfUseLink.url')}
                                onModalClose={() => this.toggleTermsOfUseModal(false)}
                                showBackground={true} />
        }
      </form>
    </Host>
  }
}
